import React, { useEffect, useState } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";

import ListingImagesSection from "./listings/ListingImagesSection";
import ListingsTable from "./listings/ListingsTable";
import ListingsToolbar from "./listings/ListingsToolbar";
import ListingEditorShell from "./listings/editor/ListingEditorShell";
import { DEFAULT_EDITOR_SECTION } from "./listings/editor/listingEditorSections";
import useListingForm from "./listings/hooks/useListingForm";
import useListingImages from "./listings/hooks/useListingImages";
import useListingsData from "./listings/hooks/useListingsData";

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

/**
 * Website listings admin screen.
 *
 * Page-level orchestrator only: it owns the mode flags, wires the three hooks
 * together and composes the toolbar, the table and the editor workspace.
 * All data access lives in the hooks, all markup in the components.
 *
 * L2 gave the overview its modern shape. L3 turned the form below it into a
 * sectioned workspace — one form, one submit, six panels that are all mounted
 * and only shown or hidden. The sections themselves are refined in L4–L6.
 */
function AdminListingsPage() {
  // Which listing is being edited, and whether the form is open. Owned here
  // because both hooks below need it and neither may depend on the other.
  const [editingListingId, setEditingListingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  // Overview search. Filtering runs in memory over the full list the admin
  // endpoint returns, so there is no request to debounce.
  const [query, setQuery] = useState("");
  // Which editor panel is visible. Presentation only — it lives here rather
  // than in the form hook because no payload and no request depends on it.
  const [activeSection, setActiveSection] = useState(DEFAULT_EDITOR_SECTION);

  // Opening the editor for a different listing, or for a new one, starts at
  // the first section again — otherwise the previous listing's section would
  // decide where the next one opens.
  useEffect(() => {
    setActiveSection(DEFAULT_EDITOR_SECTION);
  }, [editingListingId, showForm]);

  const data = useListingsData();
  // `editor` is read lazily inside this callback, which only ever runs from
  // an event handler — long after the const below is initialised.
  const images = useListingImages({
    editingListingId,
    onError: (message) => editor.setCreateError(message),
  });
  const editor = useListingForm({
    listings: data.listings,
    units: data.units,
    cities: data.cities,
    fetchListings: data.fetchListings,
    editingListingId,
    setEditingListingId,
    setShowForm,
    images,
  });

  // Publish-state sync (discovery risk R1). The editor hydrates once, in
  // handleEditClick, so a publish PATCH fired from the table below would
  // otherwise leave the open form holding the value from before it — and the
  // form's next save sends `is_published` unconditionally, silently reverting
  // the toggle.
  //
  // Deliberately NOT a listings -> form effect. A general sync would also run
  // on an availability-only PATCH and would then overwrite a publish value the
  // user set in the editor but has not saved yet. This runs only as the direct
  // consequence of a successful publish PATCH on the listing that is open, and
  // it moves exactly one field.
  const handlePatchListingStatus = async (listingId, payload) => {
    const updated = await data.patchListingStatus(listingId, payload);
    // A failed PATCH resolves to undefined — nothing was confirmed, so nothing
    // is synced and the checkbox keeps its current value.
    if (!updated) return updated;
    if (
      listingId === editingListingId &&
      Object.prototype.hasOwnProperty.call(payload, "is_published")
    ) {
      // The server's row is the authority, not the requested value.
      editor.setForm((prev) => ({ ...prev, is_published: !!updated.is_published }));
    }
    return updated;
  };

  const toggleForm = () => {
    if (showForm) {
      setShowForm(false);
    } else {
      editor.resetFormForNewListing();
      setShowForm(true);
    }
  };

  return (
    <APIProvider apiKey={MAPS_API_KEY}>
    <div
      className="content-start h-min w-full bg-[#f8fafc] text-[#0f172a] [color-scheme:light] dark:bg-[#07090f] dark:text-[#eef2ff]"
      style={{
        display: "grid",
        gap: "24px",
        alignContent: "start",
        gridAutoRows: "min-content",
      }}
    >
      <ListingsToolbar
        query={query}
        onQueryChange={setQuery}
        showForm={showForm}
        editingListingId={editingListingId}
        onToggleForm={toggleForm}
      />

      <ListingsTable
        listings={data.listings}
        units={data.units}
        cities={data.cities}
        loading={data.loading}
        error={data.error}
        query={query}
        editingListingId={editingListingId}
        statusUpdateError={data.statusUpdateError}
        statusUpdateLoadingId={data.statusUpdateLoadingId}
        onPatchStatus={handlePatchListingStatus}
        onEdit={editor.handleEditClick}
      />

      {/* The workspace only exists while something is in it — the create
          action lives in the toolbar, so an always-on card would be an empty
          box under the table. The image panels stay their own block below it
          (they bring their own card), exactly as before. */}
      {showForm || images.createdListingId ? (
        <>
          {showForm && (
            <ListingEditorShell
              editor={editor}
              data={data}
              /* Edit fills `existingImages`, create fills `uploadedImages`, and
                 never both for one listing — so the preview reads the pair. */
              images={[...images.existingImages, ...images.uploadedImages]}
              editingListingId={editingListingId}
              activeSection={activeSection}
              onSectionChange={setActiveSection}
            />
          )}

          <ListingImagesSection
            editingListingId={editingListingId}
            showForm={showForm}
            createdListingId={images.createdListingId}
            existingImages={images.existingImages}
            uploadedImages={images.uploadedImages}
            deletingImageId={images.deletingImageId}
            editUploading={images.editUploading}
            uploadingImages={images.uploadingImages}
            onDeleteExistingImage={images.handleDeleteExistingImage}
            onEditImageUpload={images.handleEditImageUpload}
            onImageUpload={images.handleImageUpload}
            onDone={editor.resetFormForNewListing}
          />
        </>
      ) : null}

    </div>
    </APIProvider>
  );
}

export default AdminListingsPage;
